import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FaUserTie,
  FaClock,
  FaTicketAlt,
  FaSync,
  FaChartPie,
  FaExclamationTriangle,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaBullseye,
  FaInfoCircle,
} from 'react-icons/fa';
import './EmployeeProductivity.css';

// Message-origin palette — aligned with the global semantic tokens
// (index.css: --state-success / --state-warning / --violet).
const ORIGIN_COLORS = {
  ai_unmodified: '#4ADE80',
  ai_modified: '#FBBF24',
  manual: '#A78BFA',
};

const CATEGORY_META = {
  ai_unmodified: {
    labelKey: 'employeeProductivity.aiUnmodified',
    labelFallback: 'IA validée telle quelle',
    color: ORIGIN_COLORS.ai_unmodified,
  },
  ai_modified: {
    labelKey: 'employeeProductivity.aiModified',
    labelFallback: 'IA retouchée avant envoi',
    color: ORIGIN_COLORS.ai_modified,
  },
  manual: {
    labelKey: 'employeeProductivity.manual',
    labelFallback: 'Rédigée manuellement',
    color: ORIGIN_COLORS.manual,
  },
};

const formatHoursMinutes = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '0 h';
  const total = Math.round(Number(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`;
};

const formatMinutes = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '—';
  const minutes = Number(seconds) / 60;
  return minutes >= 1 ? `${Math.round(minutes)} min` : `${Math.round(Number(seconds))} s`;
};

// Durations across 4 orders of magnitude (45 s … 12 j) — the insights KPIs.
const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Number(seconds);
  if (s < 90) return `${Math.round(s)} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  if (s < 172800) {
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return m > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`;
  }
  const d = Math.floor(s / 86400);
  const h = Math.round((s % 86400) / 3600);
  return h > 0 ? `${d} j ${h} h` : `${d} j`;
};

const formatNumber = (value) => {
  if (value == null || Number.isNaN(value)) return '0';
  return Number(value).toLocaleString('fr-FR');
};

const DRILLDOWN_PAGE_SIZE = 25;
const PERIODS = [7, 30, 90];

// Sentinel value in the selector for the admin's own productivity.
const ADMIN_OPTION_ID = '__admin__';

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

// Delta vs the previous period. For latencies lower is better.
const DeltaChip = ({ current, previous, lowerIsBetter = false, t }) => {
  if (current == null || previous == null || previous <= 0) return null;
  const pct = ((current - previous) / previous) * 100;
  if (!Number.isFinite(pct) || Math.abs(pct) < 1) return null;
  const improved = lowerIsBetter ? pct < 0 : pct > 0;
  return (
    <span
      className={`una-delta-chip ${improved ? 'good' : 'bad'}`}
      title={t('employeeProductivity.deltaTooltip', 'vs période précédente')}
    >
      {pct < 0 ? '▼' : '▲'} {Math.abs(Math.round(pct))} %
    </span>
  );
};

const ProportionList = ({ rows, maxRows = 6 }) => {
  const top = rows.slice(0, maxRows);
  const max = top.reduce((m, r) => Math.max(m, r.count || 0), 0);
  if (top.length === 0 || max === 0) return null;
  return (
    <div className="una-prop-list">
      {top.map((row) => (
        <div key={row.label} className="una-prop-row" title={`${row.label} — ${formatNumber(row.count)}`}>
          <span className="una-prop-label">{row.label}</span>
          <span className="una-prop-track">
            <i style={{ width: `${(row.count / max) * 100}%` }} />
          </span>
          <span className="una-prop-value">
            {formatNumber(row.count)}
            {row.percentage != null && <em>{Math.round(row.percentage)} %</em>}
          </span>
        </div>
      ))}
    </div>
  );
};

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// UTC buckets from the API shifted to the viewer's clock (whole hours — a
// close approximation across DST, stated in the tooltip).
const Heatmap = ({ cells, t }) => {
  const matrix = useMemo(() => {
    const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
    const shift = Math.round(-new Date().getTimezoneOffset() / 60);
    (cells || []).forEach((cell) => {
      const raw = (cell.hour ?? 0) + shift;
      const hour = ((raw % 24) + 24) % 24;
      const dayShift = Math.floor(raw / 24);
      const dow = ((((cell.dow ?? 1) - 1 + dayShift) % 7) + 7) % 7;
      grid[dow][hour] += cell.count || 0;
    });
    const max = grid.reduce((m, row) => Math.max(m, ...row), 0);
    return { grid, max };
  }, [cells]);

  if (matrix.max === 0) return null;
  return (
    <div
      className="una-heatmap"
      role="img"
      aria-label={t('employeeProductivity.heatmapAria', 'Messages envoyés par heure et jour')}
    >
      {matrix.grid.map((row, d) => (
        <div key={DAY_LABELS[d]} className="una-heatmap-row">
          <span className="una-heatmap-day">{DAY_LABELS[d]}</span>
          {row.map((count, h) => (
            <i
              key={h}
              style={{ opacity: count > 0 ? 0.25 + 0.75 * (count / matrix.max) : 0 }}
              className={count > 0 ? 'filled' : ''}
              title={`${DAY_LABELS[d]} ${String(h).padStart(2, '0')} h — ${formatNumber(count)}`}
            />
          ))}
        </div>
      ))}
      <div className="una-heatmap-hours" aria-hidden="true">
        <span />
        {[0, 6, 12, 18].map((h) => (
          <b key={h} style={{ left: `${(h / 24) * 100}%` }}>{h} h</b>
        ))}
      </div>
    </div>
  );
};

const EmployeeProductivity = ({ embedded = false, initialEmployeeId = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || '{}');
    } catch (err) {
      return {};
    }
  }, []);
  const userRole = userData?.role;
  const ownUserId = userData?._id || localStorage.getItem('user_id') || null;

  const canBrowseEmployees = ['admin', 'seller', 'logistics', 'logistician', 'eshop'].includes(userRole);

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(initialEmployeeId || '');
  // Period: a chip (7/30/90 days, auto-reload) or 'custom' with explicit dates.
  const [period, setPeriod] = useState(7);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const startDate = period === 'custom' ? customStart : isoDaysAgo(period);
  const endDate = period === 'custom' ? customEnd : '';

  const [productivity, setProductivity] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [insights, setInsights] = useState(null);
  const [teamComparator, setTeamComparator] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // SLA objectives modal (admin-level roles)
  const [slaModalOpen, setSlaModalOpen] = useState(false);
  const [slaForm, setSlaForm] = useState(null);
  const [slaSaving, setSlaSaving] = useState(false);
  const [slaError, setSlaError] = useState(null);

  // Jump-in from the roster ("Productivité" row action)
  useEffect(() => {
    if (initialEmployeeId) setSelectedEmployeeId(initialEmployeeId);
  }, [initialEmployeeId]);

  const fetchEmployees = useCallback(async () => {
    if (!canBrowseEmployees) return;
    setEmployeesLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/employees/agents/basic', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setEmployees(list);
      setSelectedEmployeeId((prev) => prev || (list.length > 0 ? list[0]._id : ADMIN_OPTION_ID));
    } catch (err) {
      console.error('Failed to fetch employees:', err);
      // The admin's own productivity remains selectable even without employees.
      setSelectedEmployeeId((prev) => prev || ADMIN_OPTION_ID);
    } finally {
      setEmployeesLoading(false);
    }
  }, [canBrowseEmployees]);

  const fetchProductivity = useCallback(async () => {
    const targetId = canBrowseEmployees ? selectedEmployeeId : ownUserId;
    if (!targetId) return;
    if (period === 'custom' && !customStart) return; // wait for a start date
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const qs = params.toString();
      const isAdminSelf = canBrowseEmployees && targetId === ADMIN_OPTION_ID;
      const prodUrl = isAdminSelf
        ? `/api/statistics/admin/productivity${qs ? `?${qs}` : ''}`
        : `/api/statistics/employee/${targetId}/productivity${qs ? `?${qs}` : ''}`;
      const insightsUrl = isAdminSelf
        ? `/api/statistics/admin/insights${qs ? `?${qs}` : ''}`
        : `/api/statistics/employee/${targetId}/insights${qs ? `?${qs}` : ''}`;

      const prodPromise = axios.get(prodUrl, { headers });
      // Deep insights (first response, validation latency, reliability…) —
      // admin-level roles only, like /productivity. Soft-fails.
      const insightsPromise = canBrowseEmployees
        ? axios.get(insightsUrl, { headers }).catch(() => null)
        : Promise.resolve(null);
      // Outcomes (tickets handled, resolution rate, sentiment, topics) exist
      // per employee only — the admin-self view keeps activity + insights.
      const perfPromise = !isAdminSelf && canBrowseEmployees
        ? axios.get(`/api/statistics/employee/${targetId}/performance${qs ? `?${qs}` : ''}`, { headers }).catch(() => null)
        : Promise.resolve(null);
      // Team comparator from the roster endpoint (chip periods only).
      const rosterPromise = canBrowseEmployees && period !== 'custom'
        ? axios.get(`/api/employees/roster-stats?window_days=${period}`, { headers }).catch(() => null)
        : Promise.resolve(null);

      const [prodRes, insightsRes, perfRes, rosterRes] = await Promise.all([
        prodPromise, insightsPromise, perfPromise, rosterPromise,
      ]);
      setProductivity(prodRes.data);
      setInsights(insightsRes?.data || null);
      setPerformance(perfRes?.data || null);
      setTeamComparator(rosterRes?.data?.team || null);
    } catch (err) {
      console.error('Failed to fetch employee productivity:', err);
      const status = err?.response?.status;
      if (status === 403) {
        setError(t('employeeProductivity.forbidden', 'You are not allowed to view this employee.'));
      } else if (status === 404) {
        setError(t('employeeProductivity.notFound', 'Employee not found.'));
      } else if (status === 400) {
        setError(t('employeeProductivity.badRequest', 'Invalid employee identifier.'));
      } else {
        setError(t('employeeProductivity.fetchError', 'Error loading productivity metrics.'));
      }
      setProductivity(null);
      setPerformance(null);
      setInsights(null);
    } finally {
      setIsLoading(false);
    }
  }, [canBrowseEmployees, selectedEmployeeId, ownUserId, period, customStart, startDate, endDate, t]);

  const openSlaModal = useCallback(() => {
    const sla = insights?.sla || {};
    setSlaForm({
      first_response_hours: sla.first_response_hours ?? 4,
      draft_validation_hours: sla.draft_validation_hours ?? 8,
      manual_minutes_per_reply: sla.manual_minutes_per_reply ?? 4,
    });
    setSlaError(null);
    setSlaModalOpen(true);
  }, [insights]);

  const saveSla = useCallback(async () => {
    if (!slaForm) return;
    setSlaSaving(true);
    setSlaError(null);
    try {
      const token = localStorage.getItem('access_token');
      await axios.put('/api/logistics/team-sla-config', {
        first_response_hours: Number(slaForm.first_response_hours) || null,
        draft_validation_hours: Number(slaForm.draft_validation_hours) || null,
        manual_minutes_per_reply: Number(slaForm.manual_minutes_per_reply) || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSlaModalOpen(false);
      fetchProductivity();
    } catch (err) {
      console.error('Failed to save SLA config:', err);
      setSlaError(t('employeeProductivity.slaSaveError', 'Impossible d’enregistrer les objectifs.'));
    } finally {
      setSlaSaving(false);
    }
  }, [slaForm, fetchProductivity, t]);

  // Drill-down: tickets where the employee sent messages of a given category.
  // Only reachable for roles that can browse other employees (admin-only endpoint).
  const [drilldownCategory, setDrilldownCategory] = useState(null);
  const [drilldownTickets, setDrilldownTickets] = useState([]);
  const [drilldownPage, setDrilldownPage] = useState(1);
  const [drilldownTotal, setDrilldownTotal] = useState(0);
  const [drilldownHasMore, setDrilldownHasMore] = useState(false);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState(null);

  const fetchDrilldownTickets = useCallback(
    async (category, page) => {
      if (!canBrowseEmployees || !selectedEmployeeId || !category) return;
      setDrilldownLoading(true);
      setDrilldownError(null);
      try {
        const token = localStorage.getItem('access_token');
        const params = new URLSearchParams();
        params.append('category', category);
        params.append('page', String(page));
        params.append('limit', String(DRILLDOWN_PAGE_SIZE));
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        const url =
          selectedEmployeeId === ADMIN_OPTION_ID
            ? `/api/statistics/admin/productivity/tickets?${params.toString()}`
            : `/api/statistics/employee/${selectedEmployeeId}/productivity/tickets?${params.toString()}`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = response.data || {};
        const list = Array.isArray(data)
          ? data
          : data.tickets || data.items || data.results || [];
        const total =
          typeof data.total === 'number'
            ? data.total
            : typeof data.total_count === 'number'
            ? data.total_count
            : typeof data.count === 'number'
            ? data.count
            : list.length + (page - 1) * DRILLDOWN_PAGE_SIZE;
        const hasMore =
          typeof data.has_more === 'boolean'
            ? data.has_more
            : typeof data.has_next === 'boolean'
            ? data.has_next
            : list.length >= DRILLDOWN_PAGE_SIZE;
        setDrilldownTickets(list);
        setDrilldownTotal(total);
        setDrilldownHasMore(hasMore);
      } catch (err) {
        console.error('Failed to fetch drilldown tickets:', err);
        const status = err?.response?.status;
        if (status === 403) {
          setDrilldownError(
            t('employeeProductivity.drilldownForbidden', 'You are not allowed to view these tickets.')
          );
        } else if (status === 400) {
          setDrilldownError(
            t('employeeProductivity.drilldownBadRequest', 'Invalid request.')
          );
        } else if (status === 404) {
          setDrilldownError(
            t('employeeProductivity.drilldownNotFound', 'Employee not found.')
          );
        } else {
          setDrilldownError(
            t('employeeProductivity.drilldownError', 'Error loading tickets.')
          );
        }
        setDrilldownTickets([]);
        setDrilldownHasMore(false);
        setDrilldownTotal(0);
      } finally {
        setDrilldownLoading(false);
      }
    },
    [canBrowseEmployees, selectedEmployeeId, startDate, endDate, t]
  );

  const openDrilldown = useCallback(
    (category) => {
      if (!canBrowseEmployees) return;
      setDrilldownCategory(category);
      setDrilldownPage(1);
      fetchDrilldownTickets(category, 1);
    },
    [canBrowseEmployees, fetchDrilldownTickets]
  );

  const closeDrilldown = useCallback(() => {
    setDrilldownCategory(null);
    setDrilldownTickets([]);
    setDrilldownError(null);
    setDrilldownPage(1);
    setDrilldownTotal(0);
    setDrilldownHasMore(false);
  }, []);

  const changeDrilldownPage = useCallback(
    (delta) => {
      if (!drilldownCategory) return;
      const next = drilldownPage + delta;
      if (next < 1) return;
      if (delta > 0 && !drilldownHasMore) return;
      setDrilldownPage(next);
      fetchDrilldownTickets(drilldownCategory, next);
    },
    [drilldownCategory, drilldownPage, drilldownHasMore, fetchDrilldownTickets]
  );

  useEffect(() => {
    closeDrilldown();
  }, [selectedEmployeeId, period, customStart, customEnd, closeDrilldown]);

  useEffect(() => {
    if (!drilldownCategory) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDrilldown();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drilldownCategory, closeDrilldown]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchProductivity();
  }, [fetchProductivity]);

  const breakdownRows = useMemo(() => {
    if (!productivity) return [];
    const total = productivity.total_sent_messages || 0;
    return ['ai_unmodified', 'ai_modified', 'manual'].map((key) => {
      const value = key === 'ai_unmodified'
        ? productivity.ai_validated_unmodified || 0
        : key === 'ai_modified'
          ? productivity.ai_validated_modified || 0
          : productivity.manual_messages || 0;
      const meta = CATEGORY_META[key];
      return {
        key,
        label: t(meta.labelKey, meta.labelFallback),
        value,
        color: meta.color,
        percentage: total > 0 ? (value / total) * 100 : 0,
      };
    });
  }, [productivity, t]);

  const totalSent = productivity?.total_sent_messages || 0;

  const daily = useMemo(() => {
    const rows = productivity?.daily || [];
    const max = rows.reduce((m, row) => Math.max(m, row.count || 0), 0);
    return { rows, max };
  }, [productivity]);

  const selectedName = useMemo(() => {
    if (selectedEmployeeId === ADMIN_OPTION_ID) {
      return userData?.name || t('employeeProductivity.adminSelf', 'Moi (admin)');
    }
    const emp = employees.find((e) => e._id === selectedEmployeeId);
    return emp?.name || emp?.email || '';
  }, [selectedEmployeeId, employees, userData, t]);

  const selectedEmail = useMemo(() => {
    if (selectedEmployeeId === ADMIN_OPTION_ID) return userData?.email || '';
    return employees.find((e) => e._id === selectedEmployeeId)?.email || '';
  }, [selectedEmployeeId, employees, userData]);

  const isAdminSelf = selectedEmployeeId === ADMIN_OPTION_ID;
  const teamRate = teamComparator?.resolution_rate_window;
  const sla = insights?.sla || null;
  const firstResponse = insights?.first_response || null;
  const validation = insights?.draft_validation || null;
  const closures = insights?.closures || null;
  const reopens = insights?.reopens || null;
  const escalations = insights?.escalations || null;
  const queue = insights?.queue || null;

  const aiValidatedTotal = (productivity?.ai_validated_unmodified || 0) + (productivity?.ai_validated_modified || 0);
  const timeSavedSeconds = sla && aiValidatedTotal > 0
    ? aiValidatedTotal * (sla.manual_minutes_per_reply || 0) * 60
    : null;

  const topicRows = useMemo(() => (
    (performance?.topic_breakdown || [])
      .filter((row) => row.topic)
      .map((row) => ({ label: row.topic, count: row.count, percentage: row.percentage }))
  ), [performance]);

  const carrierRows = useMemo(() => (
    (insights?.carrier_mix || []).map((row) => ({
      label: row.carrier, count: row.count, percentage: row.percentage,
    }))
  ), [insights]);

  const initials = useMemo(() => (
    (selectedName || '?')
      .split(/\s+/)
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase()
  ), [selectedName]);

  return (
    <div className={`una-productivity-container${embedded ? ' embedded' : ''}`}>
      <div className="una-productivity-header">
        <div className="una-productivity-title">
          {!embedded && (
            <>
              <FaUserTie className="una-header-icon" />
              <h1>{t('employeeProductivity.title', 'Productivité')}</h1>
            </>
          )}
        </div>

        <div className="una-productivity-filters">
          {canBrowseEmployees && (
            <select
              className="una-prod-select"
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              disabled={employeesLoading}
              aria-label={t('employeeProductivity.employee', 'Employé')}
            >
              {employeesLoading && employees.length === 0 && (
                <option value="">{t('employeeProductivity.loadingEmployees', 'Chargement…')}</option>
              )}
              <option value={ADMIN_OPTION_ID}>
                {t('employeeProductivity.adminSelf', 'Moi (admin)')}
                {userData?.name ? ` — ${userData.name}` : ''}
              </option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.name || emp.email || emp._id}
                </option>
              ))}
            </select>
          )}

          <div className="una-period-chips" role="group" aria-label={t('employeeProductivity.period', 'Période')}>
            {PERIODS.map((days) => (
              <button
                key={days}
                type="button"
                className={period === days ? 'active' : ''}
                aria-pressed={period === days}
                onClick={() => setPeriod(days)}
              >
                {days} j
              </button>
            ))}
            <button
              type="button"
              className={period === 'custom' ? 'active' : ''}
              aria-pressed={period === 'custom'}
              onClick={() => setPeriod('custom')}
            >
              {t('employeeProductivity.custom', 'Personnalisé')}
            </button>
          </div>

          {period === 'custom' && (
            <div className="una-custom-dates">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                max={customEnd || undefined}
                aria-label={t('employeeProductivity.startDate', 'Début')}
              />
              <span>→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                min={customStart || undefined}
                aria-label={t('employeeProductivity.endDate', 'Fin')}
              />
            </div>
          )}

          {canBrowseEmployees && insights && (
            <button type="button" className="una-sla-btn" onClick={openSlaModal}>
              <FaBullseye aria-hidden="true" />
              {t('employeeProductivity.slaButton', 'Objectifs')}
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="una-productivity-loading">
          <FaSync className="fa-spin" />
          {t('employeeProductivity.loading', 'Chargement des métriques…')}
        </div>
      )}

      {error && !isLoading && (
        <div className="una-productivity-error">
          <FaExclamationTriangle />
          {error}
        </div>
      )}

      {!isLoading && !error && !productivity && (
        <div className="una-empty-state">
          <FaChartPie />
          <h3>{t('employeeProductivity.emptyTitle', 'No data to display')}</h3>
          <p>
            {canBrowseEmployees
              ? t('employeeProductivity.emptyDescAdmin', 'Select an employee to view their productivity metrics.')
              : t('employeeProductivity.emptyDescEmployee', 'Productivity metrics will appear here once activity has been recorded.')}
          </p>
        </div>
      )}

      {!isLoading && !error && productivity && (
        <>
          {/* Identity strip: who this is + the live state of their queue */}
          {selectedName && (
            <div className="una-ident-strip">
              <span className="una-ident-avatar" aria-hidden="true">{initials}</span>
              <div className="una-ident-who">
                <b>{selectedName}</b>
                {selectedEmail && <span>{selectedEmail}</span>}
              </div>
              {queue && (
                <div className="una-ident-queue">
                  <span className="una-ident-stat">
                    <b>{formatNumber(queue.open)}</b> {t('employeeProductivity.queueOpen', 'tickets ouverts assignés')}
                  </span>
                  {queue.oldest_days != null && (
                    <span className="una-ident-stat">
                      {t('employeeProductivity.queueOldest', 'plus ancien : {{days}} j', { days: queue.oldest_days })}
                    </span>
                  )}
                  {queue.median_age_days != null && (
                    <span className="una-ident-stat">
                      {t('employeeProductivity.queueMedian', 'âge médian : {{days}} j', { days: queue.median_age_days })}
                    </span>
                  )}
                  {validation && validation.pending_now > 0 && (
                    <span className="una-ident-stat pending">
                      <b>{formatNumber(validation.pending_now)}</b>{' '}
                      {t('employeeProductivity.pendingDrafts', 'brouillons IA en attente')}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Outcome + loop KPIs. Cells render only when their data exists. */}
          <div className="una-kpi-grid">
            {!isAdminSelf && performance && (
              <div className="una-kpi-card">
                <span className="una-kpi-eyebrow">{t('employeeProductivity.kpiHandled', 'Tickets traités')}</span>
                <span className="una-kpi-value">{formatNumber(performance.total_tickets)}</span>
                <span className="una-kpi-sub">{t('employeeProductivity.kpiHandledSub', 'assignés sur la période')}</span>
              </div>
            )}
            {!isAdminSelf && performance && (
              <div className="una-kpi-card">
                <span className="una-kpi-eyebrow">{t('employeeProductivity.kpiResolution', 'Taux de résolution')}</span>
                <span className="una-kpi-value">
                  {Math.round(performance.resolution_rate || 0)}<span className="una-kpi-unit">%</span>
                  {teamRate !== null && teamRate !== undefined && (
                    <span className="una-kpi-badge">{t('employeeProductivity.teamRate', 'équipe : {{rate}} %', { rate: teamRate })}</span>
                  )}
                </span>
                <span className="una-kpi-sub">{t('employeeProductivity.kpiResolutionSub', 'statuts terminaux, toutes variantes')}</span>
              </div>
            )}
            {firstResponse && firstResponse.median_s != null && (
              <div className="una-kpi-card">
                <span className="una-kpi-eyebrow">{t('employeeProductivity.kpiFirstResponse', '1re réponse (médiane)')}</span>
                <span className="una-kpi-value">
                  {formatDuration(firstResponse.median_s)}
                  <DeltaChip current={firstResponse.median_s} previous={firstResponse.prev_median_s} lowerIsBetter t={t} />
                </span>
                <span className="una-kpi-sub">
                  {firstResponse.sla_share != null && sla
                    ? t('employeeProductivity.kpiFirstResponseSub', '{{share}} % ≤ {{hours}} h · {{count}} réponses', {
                        share: firstResponse.sla_share, hours: sla.first_response_hours, count: formatNumber(firstResponse.count),
                      })
                    : t('employeeProductivity.kpiFirstResponseCount', '{{count}} réponses clients', { count: formatNumber(firstResponse.count) })}
                </span>
              </div>
            )}
            {validation && validation.median_s != null && (
              <div className="una-kpi-card">
                <span className="una-kpi-eyebrow">{t('employeeProductivity.kpiValidation', 'Validation des brouillons IA')}</span>
                <span className="una-kpi-value">
                  {formatDuration(validation.median_s)}
                  <DeltaChip current={validation.median_s} previous={validation.prev_median_s} lowerIsBetter t={t} />
                </span>
                <span className="una-kpi-sub">
                  {validation.sla_share != null && sla
                    ? t('employeeProductivity.kpiValidationSub', '{{share}} % ≤ {{hours}} h · {{count}} brouillons validés', {
                        share: validation.sla_share, hours: sla.draft_validation_hours, count: formatNumber(validation.count),
                      })
                    : t('employeeProductivity.kpiValidationCount', '{{count}} brouillons validés', { count: formatNumber(validation.count) })}
                </span>
              </div>
            )}
            {!isAdminSelf && performance && performance.avg_sentiment > 0 && (
              <div className="una-kpi-card">
                <span className="una-kpi-eyebrow">{t('employeeProductivity.kpiMood', 'Humeur clients')}</span>
                <span className="una-kpi-value">{Math.round(performance.avg_sentiment)}<span className="una-kpi-unit">/100</span></span>
                <span className="una-kpi-sub">{t('employeeProductivity.kpiMoodSub', 'sentiment moyen sur ses tickets')}</span>
              </div>
            )}
            {timeSavedSeconds != null && (
              <div
                className="una-kpi-card"
                title={t('employeeProductivity.timeSavedTooltip', 'Estimation : chaque réponse validée depuis un brouillon IA économise le temps de rédaction manuelle défini dans les objectifs ({{minutes}} min).', { minutes: sla.manual_minutes_per_reply })}
              >
                <span className="una-kpi-eyebrow">
                  {t('employeeProductivity.kpiTimeSaved', 'Temps gagné grâce à l’IA')}
                  <FaInfoCircle className="una-kpi-info" aria-hidden="true" />
                </span>
                <span className="una-kpi-value">{formatDuration(timeSavedSeconds)}</span>
                <span className="una-kpi-sub">
                  {t('employeeProductivity.kpiTimeSavedSub', '{{count}} réponses IA × {{minutes}} min', {
                    count: formatNumber(aiValidatedTotal), minutes: sla.manual_minutes_per_reply,
                  })}
                </span>
              </div>
            )}
            <div className="una-kpi-card">
              <span className="una-kpi-eyebrow">{t('employeeProductivity.kpiTime', 'Temps sur tickets')}</span>
              <span className="una-kpi-value">{formatHoursMinutes(productivity.total_time_on_tickets_seconds)}</span>
              <span className="una-kpi-sub">
                {t('employeeProductivity.kpiTimeSub', '{{tickets}} tickets visités · {{avg}} / ticket en moy.', {
                  tickets: formatNumber(productivity.tickets_visited),
                  avg: formatMinutes(productivity.avg_time_on_ticket_seconds),
                })}
              </span>
            </div>
          </div>

          <div className="una-productivity-grid">
            <div className="una-productivity-card">
              <h3>{t('employeeProductivity.messageOrigin', 'Origine des réponses envoyées')}</h3>
              {totalSent > 0 ? (
                <>
                  <div className="una-comp-bar" role="img" aria-label={t('employeeProductivity.messageOrigin', 'Origine des réponses envoyées')}>
                    {breakdownRows.filter((row) => row.value > 0).map((row) => (
                      <span key={row.key} style={{ flexGrow: row.value, backgroundColor: row.color }} title={`${row.label} — ${formatNumber(row.value)}`} />
                    ))}
                  </div>
                  <div className="una-comp-legend">
                    {breakdownRows.map((row) => {
                      const clickable = canBrowseEmployees && row.value > 0;
                      return (
                        <div
                          key={row.key}
                          className={`una-comp-item${clickable ? ' clickable' : ''}`}
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                          onClick={clickable ? () => openDrilldown(row.key) : undefined}
                          onKeyDown={clickable ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrilldown(row.key); }
                          } : undefined}
                        >
                          <span className="una-comp-swatch" style={{ backgroundColor: row.color }} aria-hidden="true" />
                          <span className="una-comp-label">{row.label}</span>
                          <span className="una-comp-pct">{Math.round(row.percentage)} %</span>
                          <b>{formatNumber(row.value)}</b>
                          {clickable && (
                            <span className="una-comp-link">
                              {t('employeeProductivity.viewTickets', 'voir les tickets')} <FaExternalLinkAlt aria-hidden="true" />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="una-comp-footer">
                    <span><b>{formatNumber(totalSent)}</b> {t('employeeProductivity.totalSent', 'messages envoyés')}</span>
                    <span><b>{formatNumber(productivity.total_edits)}</b> {t('employeeProductivity.totalEdits', 'retouches au total')}</span>
                  </div>
                </>
              ) : (
                <p className="una-card-empty">{t('employeeProductivity.noMessages', 'Aucun message envoyé sur la période.')}</p>
              )}
            </div>

            {(closures || reopens || escalations) && (
              <div className="una-productivity-card">
                <h3>{t('employeeProductivity.reliability', 'Fiabilité & réactivité')}</h3>
                <div className="una-reliability">
                  {closures && (closures.by_human + closures.by_ai + closures.other > 0) && (
                    <div className="una-rel-row">
                      <span className="una-rel-label">{t('employeeProductivity.relClosures', 'Clôtures sur ses tickets')}</span>
                      <span className="una-rel-value">
                        <b>{formatNumber(closures.by_human)}</b> {t('employeeProductivity.relByTeam', 'par l’équipe')}
                        {' · '}
                        <b>{formatNumber(closures.by_ai)}</b> {t('employeeProductivity.relByAi', 'par l’IA')}
                      </span>
                    </div>
                  )}
                  {reopens && (
                    <div
                      className="una-rel-row"
                      title={t('employeeProductivity.reopensTooltip', 'Réouvertures suite à une réponse client sur un ticket clôturé — mesurées depuis octobre 2026.')}
                    >
                      <span className="una-rel-label">{t('employeeProductivity.relReopens', 'Réouvertures après clôture')}</span>
                      <span className={`una-rel-value${reopens.count > 0 ? ' warn' : ''}`}>
                        <b>{formatNumber(reopens.count)}</b>
                        {reopens.count > 0 && (
                          <> — {t('employeeProductivity.relReopensHuman', '{{count}} après une clôture humaine', { count: reopens.after_human_closure })}</>
                        )}
                      </span>
                    </div>
                  )}
                  {escalations && escalations.count > 0 && (
                    <div className="una-rel-row">
                      <span className="una-rel-label">{t('employeeProductivity.relEscalations', 'Escalades reçues de l’IA')}</span>
                      <span className="una-rel-value">
                        <b>{formatNumber(escalations.count)}</b>
                        {' · '}
                        {t('employeeProductivity.relEscalationsHandled', '{{handled}} prises en charge', { handled: escalations.handled })}
                        {escalations.median_reaction_s != null && (
                          <> · {t('employeeProductivity.relEscalationsReaction', 'réaction médiane {{duration}}', { duration: formatDuration(escalations.median_reaction_s) })}</>
                        )}
                      </span>
                    </div>
                  )}
                  {escalations && escalations.count === 0 && reopens && reopens.count === 0 && (!closures || closures.by_human + closures.by_ai + closures.other === 0) && (
                    <p className="una-card-empty">{t('employeeProductivity.relEmpty', 'Rien à signaler sur la période.')}</p>
                  )}
                </div>
              </div>
            )}

            {(topicRows.length > 0 || carrierRows.length > 0) && (
              <div className="una-productivity-card">
                <h3>{t('employeeProductivity.workMix', 'Répartition du travail')}</h3>
                <div className="una-mix-columns">
                  {topicRows.length > 0 && (
                    <div className="una-mix-col">
                      <h4>{t('employeeProductivity.mixTopics', 'Motifs')}</h4>
                      <ProportionList rows={topicRows} />
                    </div>
                  )}
                  {carrierRows.length > 0 && (
                    <div className="una-mix-col">
                      <h4>{t('employeeProductivity.mixCarriers', 'Transporteurs')}</h4>
                      <ProportionList rows={carrierRows} />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="una-productivity-card">
              <h3>{t('employeeProductivity.dailyActivity', 'Activité par jour — messages envoyés')}</h3>
              {daily.rows.length > 0 && daily.max > 0 ? (
                <div className="una-daily-bars" role="img" aria-label={t('employeeProductivity.dailyActivity', 'Activité par jour — messages envoyés')}>
                  {daily.rows.map((row) => (
                    <i
                      key={row.date}
                      style={{ height: `${Math.max(4, (row.count / daily.max) * 100)}%` }}
                      title={`${row.date} — ${formatNumber(row.count)}`}
                    />
                  ))}
                </div>
              ) : (
                <p className="una-card-empty">{t('employeeProductivity.noMessages', 'Aucun message envoyé sur la période.')}</p>
              )}
              <div className="una-daily-meta">
                <FaClock aria-hidden="true" />
                {t('employeeProductivity.visitsMeta', '{{visits}} visites de tickets sur la période', { visits: formatNumber(productivity.visits_count) })}
              </div>
            </div>

            {insights?.heatmap && insights.heatmap.length > 0 && (
              <div className="una-productivity-card una-heatmap-card">
                <h3
                  title={t('employeeProductivity.heatmapTooltip', 'Heure locale (approximation à l’heure près autour des changements d’heure).')}
                >
                  {t('employeeProductivity.heatmapTitle', 'Rythme — messages par heure et jour')}
                </h3>
                <Heatmap cells={insights.heatmap} t={t} />
              </div>
            )}
          </div>
        </>
      )}

      {slaModalOpen && slaForm && (
        <div
          className="una-drilldown-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setSlaModalOpen(false); }}
        >
          <div className="una-sla-modal" role="dialog" aria-modal="true">
            <div className="una-drilldown-header">
              <div className="una-drilldown-title">
                <FaBullseye aria-hidden="true" />
                <h2>{t('employeeProductivity.slaTitle', 'Objectifs de l’équipe')}</h2>
              </div>
              <button
                type="button"
                className="una-drilldown-close"
                onClick={() => setSlaModalOpen(false)}
                aria-label={t('employeeProductivity.close', 'Close')}
              >
                <FaTimes />
              </button>
            </div>
            <div className="una-sla-body">
              <p className="una-sla-hint">
                {t('employeeProductivity.slaHint', 'Ces objectifs s’appliquent à toute l’équipe et alimentent les parts « dans l’objectif » de cette page.')}
              </p>
              <label>
                {t('employeeProductivity.slaFirstResponse', 'Première réponse au client (heures)')}
                <input
                  type="number" min="0.25" max="720" step="0.25"
                  value={slaForm.first_response_hours}
                  onChange={(e) => setSlaForm({ ...slaForm, first_response_hours: e.target.value })}
                />
              </label>
              <label>
                {t('employeeProductivity.slaValidation', 'Validation d’un brouillon IA (heures)')}
                <input
                  type="number" min="0.25" max="720" step="0.25"
                  value={slaForm.draft_validation_hours}
                  onChange={(e) => setSlaForm({ ...slaForm, draft_validation_hours: e.target.value })}
                />
              </label>
              <label>
                {t('employeeProductivity.slaManualMinutes', 'Temps de rédaction manuelle d’une réponse (minutes)')}
                <input
                  type="number" min="0.5" max="60" step="0.5"
                  value={slaForm.manual_minutes_per_reply}
                  onChange={(e) => setSlaForm({ ...slaForm, manual_minutes_per_reply: e.target.value })}
                />
              </label>
              {slaError && <div className="una-productivity-error">{slaError}</div>}
            </div>
            <div className="una-sla-footer">
              <button type="button" className="una-sla-cancel" onClick={() => setSlaModalOpen(false)}>
                {t('employeeProductivity.slaCancel', 'Annuler')}
              </button>
              <button type="button" className="una-sla-save" onClick={saveSla} disabled={slaSaving}>
                {slaSaving
                  ? t('employeeProductivity.slaSaving', 'Enregistrement…')
                  : t('employeeProductivity.slaSave', 'Enregistrer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {drilldownCategory && (
        <DrilldownModal
          category={drilldownCategory}
          tickets={drilldownTickets}
          page={drilldownPage}
          total={drilldownTotal}
          hasMore={drilldownHasMore}
          loading={drilldownLoading}
          error={drilldownError}
          onClose={closeDrilldown}
          onPrev={() => changeDrilldownPage(-1)}
          onNext={() => changeDrilldownPage(1)}
          onOpenTicket={(ticketId) => {
            closeDrilldown();
            navigate(`/tickets/${ticketId}/messages`);
          }}
          t={t}
        />
      )}
    </div>
  );
};

const getTicketId = (ticket) =>
  ticket?._id || ticket?.id || ticket?.ticket_id || null;

const formatTicketDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const DrilldownModal = ({
  category,
  tickets,
  page,
  total,
  hasMore,
  loading,
  error,
  onClose,
  onPrev,
  onNext,
  onOpenTicket,
  t,
}) => {
  const meta = CATEGORY_META[category];
  const categoryLabel = meta ? t(meta.labelKey, meta.labelFallback) : category;

  return (
    <div
      className="una-drilldown-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="una-drilldown-modal" role="dialog" aria-modal="true">
        <div className="una-drilldown-header">
          <div className="una-drilldown-title">
            <span
              className="una-comp-swatch"
              style={{ backgroundColor: meta?.color || '#3FBDB4' }}
            />
            <h2>
              {t('employeeProductivity.drilldownTitle', 'Tickets —')} {categoryLabel}
            </h2>
            {total > 0 && (
              <span className="una-drilldown-count">
                {formatNumber(total)}{' '}
                {t('employeeProductivity.ticketsLabel', 'tickets')}
              </span>
            )}
          </div>
          <button
            type="button"
            className="una-drilldown-close"
            onClick={onClose}
            aria-label={t('employeeProductivity.close', 'Close')}
          >
            <FaTimes />
          </button>
        </div>

        <div className="una-drilldown-body">
          {loading && (
            <div className="una-productivity-loading">
              <FaSync className="fa-spin" />
              {t('employeeProductivity.loadingTickets', 'Loading tickets…')}
            </div>
          )}

          {!loading && error && (
            <div className="una-productivity-error">
              <FaExclamationTriangle />
              {error}
            </div>
          )}

          {!loading && !error && tickets.length === 0 && (
            <div className="una-empty-state">
              <FaTicketAlt />
              <h3>{t('employeeProductivity.noTickets', 'No tickets found')}</h3>
              <p>
                {t(
                  'employeeProductivity.noTicketsDesc',
                  'No tickets match this category for the selected period.'
                )}
              </p>
            </div>
          )}

          {!loading && !error && tickets.length > 0 && (
            <table className="una-drilldown-table">
              <thead>
                <tr>
                  <th>{t('employeeProductivity.subject', 'Subject')}</th>
                  <th>{t('employeeProductivity.customer', 'Customer')}</th>
                  <th>{t('employeeProductivity.topic', 'Topic')}</th>
                  <th>{t('employeeProductivity.status', 'Status')}</th>
                  <th>{t('employeeProductivity.sentAt', 'Sent at')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket, idx) => {
                  const id = getTicketId(ticket);
                  const customer =
                    ticket.customer_name ||
                    ticket.customer_email ||
                    ticket.customer ||
                    '—';
                  return (
                    <tr
                      key={id || idx}
                      onClick={() => id && onOpenTicket(id)}
                      className={id ? 'clickable' : undefined}
                    >
                      <td>
                        <div className="una-drilldown-subject">
                          {ticket.subject || ticket.title || `#${id?.slice?.(-6) || idx + 1}`}
                        </div>
                      </td>
                      <td>{customer}</td>
                      <td>{ticket.topic || '—'}</td>
                      <td>
                        <span className="una-drilldown-status">
                          {ticket.status || '—'}
                        </span>
                      </td>
                      <td>{formatTicketDate(ticket.sent_at || ticket.last_sent_at || ticket.updated_at)}</td>
                      <td className="una-drilldown-action">
                        {id && <FaExternalLinkAlt />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="una-drilldown-footer">
          <div className="una-drilldown-page-info">
            {t('employeeProductivity.page', 'Page')} {page}
          </div>
          <div className="una-drilldown-pagination">
            <button
              type="button"
              className="una-drilldown-page-btn"
              onClick={onPrev}
              disabled={loading || page <= 1}
            >
              <FaChevronLeft />
              {t('employeeProductivity.previous', 'Previous')}
            </button>
            <button
              type="button"
              className="una-drilldown-page-btn"
              onClick={onNext}
              disabled={loading || !hasMore}
            >
              {t('employeeProductivity.next', 'Next')}
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeProductivity;
